// ============================================================
// PAYMENT PLANS TABLE COMPONENT
// ============================================================

import { Clock, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { PAYMENT_PLAN_STATUS_CONFIG } from '../../constants/payment-plans.constants';
import type { PaymentPlan } from '../../types/payment.types';

interface PaymentPlansTableProps {
  paymentPlans: PaymentPlan[];
  hasActiveFilters: boolean;
  onReset: () => void;
}

export function PaymentPlansTable({ paymentPlans, hasActiveFilters, onReset }: PaymentPlansTableProps) {
  const navigate = useNavigate();
  const hasNoResults = paymentPlans.length === 0;

  const handlePlanClick = (plan: PaymentPlan) => {
    navigate(`/payment-plans/${plan.id}`);
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm text-left">
        <TableHeader />
        <tbody className="divide-y divide-slate-100 text-slate-700">
          {hasNoResults ? (
            <EmptyState hasActiveFilters={hasActiveFilters} onReset={onReset} />
          ) : (
            paymentPlans.map((plan) => <PaymentPlanRow key={plan.id} plan={plan} onClick={handlePlanClick} />)
          )}
        </tbody>
      </table>
    </div>
  );
}

function TableHeader() {
  return (
    <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-semibold text-xs uppercase tracking-wider whitespace-nowrap">
      <tr>
        <th className="px-5 py-3.5 pl-6 text-left">Client</th>
        <th className="px-5 py-3.5 text-left">Référence</th>
        <th className="px-5 py-3.5 text-left">Montant Total</th>
        <th className="px-5 py-3.5 text-left">Mensualités</th>
        <th className="px-5 py-3.5 text-left">Progression</th>
        <th className="px-5 py-3.5 text-left">Statut</th>
        <th className="px-5 py-3.5 pr-6 text-right">Actions</th>
      </tr>
    </thead>
  );
}

function PaymentPlanRow({ plan, onClick }: { plan: PaymentPlan; onClick: (plan: PaymentPlan) => void }) {
  const paidCount = plan.installments?.filter((i) => i.status === 'PAID').length || 0;
  const totalCount = plan.installments?.length || 0;

  // Calcul de la progression en pourcentage
  // Si paiement FULL et statut COMPLETED -> 100%
  // Sinon calcul classique sur les échéances
  let progressPercent = 0;
  if (plan.paymentMode === 'FULL') {
    progressPercent = plan.status === 'COMPLETED' ? 100 : 0;
  } else {
    progressPercent = totalCount > 0 ? (paidCount / totalCount) * 100 : 0;
  }

  return (
    <tr onClick={() => onClick(plan)} className="hover:bg-slate-50 cursor-pointer transition-colors">
      <td className="px-5 py-4 pl-6">
        <div className="font-medium text-slate-900">{plan.booking?.customerName}</div>
        <div className="text-xs text-slate-500">{plan.booking?.customerEmail}</div>
      </td>
      <td className="px-5 py-4">
        <div className="font-mono text-xs">{plan.booking?.reference}</div>
      </td>
      <td className="px-5 py-4">
        <div className="font-semibold">{plan.totalAmount.toLocaleString('fr-FR')} XAF</div>
      </td>
      <td className="px-5 py-4">
        {plan.paymentMode === 'FULL' ? (
          <div className="text-slate-500 italic text-sm">Aucune</div>
        ) : (
          <>
            <div className="text-slate-700">{plan.monthlyAmount?.toLocaleString('fr-FR') || '0'} XAF</div>
            <div className="text-xs text-slate-500">x {totalCount} mois</div>
          </>
        )}
      </td>
      <td className="px-5 py-4">
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all ${progressPercent === 100 ? 'bg-emerald-500' : 'bg-blue-500'}`}
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <span className="text-xs text-slate-600 font-medium">{Math.round(progressPercent)}%</span>
        </div>
      </td>
      <td className="px-5 py-4">
        <StatusBadge status={plan.status} />
      </td>
      <td className="px-5 py-4 pr-6 text-right">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClick(plan);
          }}
          className="text-[#00758d] hover:text-[#00303c] font-medium text-xs"
        >
          Détails →
        </button>
      </td>
    </tr>
  );
}

function StatusBadge({ status }: { status: string }) {
  const config = PAYMENT_PLAN_STATUS_CONFIG[status as keyof typeof PAYMENT_PLAN_STATUS_CONFIG] || {
    icon: Clock,
    label: 'Statut inconnu',
    className: 'bg-slate-100 text-slate-700',
  };
  const Icon = config.icon;

  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${config.className}`}
    >
      <Icon className="w-3.5 h-3.5" />
      {config.label}
    </span>
  );
}

function EmptyState({ hasActiveFilters, onReset }: { hasActiveFilters: boolean; onReset: () => void }) {
  return (
    <tr>
      <td colSpan={7} className="px-5 py-16 text-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center">
            <Filter className="w-8 h-8 text-slate-400" />
          </div>
          <div>
            <p className="text-slate-700 font-semibold text-base mb-1">
              {hasActiveFilters ? 'Aucun résultat trouvé' : 'Aucun plan de paiement'}
            </p>
            <p className="text-slate-500 text-sm">
              {hasActiveFilters
                ? 'Essayez de modifier vos critères de recherche'
                : 'Les plans de paiement apparaîtront ici'}
            </p>
          </div>
          {hasActiveFilters && (
            <button
              onClick={onReset}
              className="mt-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}
